import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styled from "styled-components";

const MenuContainer = styled.div`
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 5px;
    overflow: hidden;
`;

const MenuTitle = styled.div`
    background-color: #0066cc;
    color: white;
    padding: 15px 20px;
    font-size: 18px;
    font-weight: bold;
    word-break: keep-all;
    word-wrap: break-word;
`;

const MenuList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const MenuItem = styled.li`
    border-bottom: 1px solid #e0e0e0;
    position: relative;
    
    &:last-child {
        border-bottom: none;
    }
`;

const MenuLink = styled(NavLink)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    color: #333;
    text-decoration: none;
    transition: all 0.2s ease;
    word-break: keep-all;
    word-wrap: break-word;
    white-space: normal;

    &:hover {
        background-color: #f5f5f5;
    }

    &.active {
        background-color: #e6f2ff;
        color: #0066cc;
        font-weight: bold;
        border-left: 4px solid #0066cc;
    }
`;

// 부모 메뉴 아이템을 위한 스타일드 컴포넌트 추가
const ParentMenuButton = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    color: #333;
    text-decoration: none;
    transition: all 0.2s ease;
    cursor: pointer;
    background-color: ${props => props.isActive ? '#e6f2ff' : 'transparent'};
    color: ${props => props.isActive ? '#0066cc' : '#333'};
    font-weight: ${props => props.isActive ? 'bold' : 'normal'};
    border-left: ${props => props.isActive ? '4px solid #0066cc' : 'none'};
    word-break: keep-all;
    word-wrap: break-word;
    white-space: normal;

    &:hover {
        background-color: #f5f5f5;
    }
`;

const SubMenuList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    background-color: #f9f9f9;
    display: ${props => (props.show ? "block" : "none")};
`;

const SubMenuItem = styled.li`
    border-top: 1px solid #eaeaea;
`;

const SubMenuLink = styled(NavLink)`
    display: block;
    padding: 10px 20px 10px 40px;
    color: #444;
    text-decoration: none;
    font-size: 0.95em;
    transition: all 0.2s ease;
    word-break: keep-all;
    word-wrap: break-word;
    white-space: normal;

    &:hover {
        background-color: #f0f0f0;
    }

    &.active {
        color: #0066cc;
        font-weight: bold;
        background-color: #e6f2ff;
    }
`;

const ExpandIcon = styled.span`
    font-size: 12px;
    transform: ${props => (props.expanded ? "rotate(180deg)" : "rotate(0)")};
    transition: transform 0.2s ease;
    flex-shrink: 0;
    margin-left: 8px;
`;

// Updated menu data with submenus
export const menuData = {
    rules: {
        title: "",
        items: [
            {id: "policy", name: "이용약관", path:"/rules/policy"},
            {id: "privacy", name: "개인정보취급방침", path:"/rules/privacy"},
        ]
    },
    intro: {
        title: "intro",
        items: [
            {id: "login", name: "로그인", path:"/intro/login"},
            {id: "signup", name: "회원가입", path:"/intro/signup"},
        ]
    },
    introduction: {
        title: "총학생회 소개",
        items: [
            { id: "greeting", name: "총학생회장단 인사말", path: "/introduction/greeting" },
            { id: "executive", name: "중앙집행위원회 소개", path: "/introduction/executive" },
            { id: "committees", name: "산하위원회 소개", path: "/introduction/committees" },
            { id: "location", name: "오시는 길", path: "/introduction/location" }
        ]
    },
    news: {
        title: "총학생회 소식",
        items: [
            {
                id: "student-council",
                name: "중앙집행위원회 소식",
                path: "/news/student-council",
                hasSubmenu: true,
                subItems: [
                    { id: "edu-council", name: "교육자치국", path: "/news/edu-council" },
                    { id: "unity-council", name: "연대사업국", path: "/news/unity-council" },
                    { id: "life-council", name: "일상사업국", path: "/news/life-council" },
                    { id: "strategy-council", name: "전략정책국", path: "/news/strategy-council" },
                    { id: "promo-council", name: "홍보소통국", path: "/news/promo-council" },
                    { id: "accounting", name: "회계사무국", path: "/news/accounting" }
                ]
            },
            {
                id: "committee",
                name: "산하위원회 소식",
                path: "/news/committee",
                hasSubmenu: true,
                subItems: [
                    { id: "culture-committee", name: "문화위원회 소식", path: "/news/culture-committee" },
                    { id: "graduation-committee", name: "졸업준비위원회 소식", path: "/news/graduation-committee" },
                    { id: "welfare-committee", name: "학생복지위원회 소식", path: "/news/welfare-committee" },
                    { id: "human-rights-committee", name: "학생인권위원회 소식", path: "/news/human-rights-committee" }
                ]
            },
            { id: "calendar", name: "연간 일정", path: "/news/calendar" }
        ]
    },
    petition: {
        title: "중대 청원",
        items: [
            { id: "create", name: "청원하기", path: "/petition/create" },
            { id: "registered", name: "등록 안건", path: "/petition/registered" },
            { id: "awaiting", name: "답변대기 안건", path: "/petition/awaiting" },
            { id: "completed", name: "답변완료 안건", path: "/petition/completed" }
        ]
    },
    "student-autonomy": {
        title: "학생 자치",
        items: [
            { id: "activity", name: "자치활동보고", path: "/student-autonomy/activity" },
            { id: "council-rules", name: "총학생회 회칙 및 세칙", path: "/student-autonomy/council-rules" },
            { id: "central-committee", name: "중앙운영위원회 회의록", path: "/student-autonomy/central-committee" },
            { id: "department-rules", name: "단과대학 학생 회칙 및 세칙", path: "/student-autonomy/department-rules" }
        ]
    },
    education: {
        title: "교육 및 학사",
        items: [
            { id: "academic-info", name: "학사정보", path: "/education/academic-info" },
            { id: "major-graduation", name: "다전공 및 졸업요건", path: "/education/major-graduation" },
            { id: "free-board", name: "자유게시판", path: "/education/free-board" }
        ]
    },
    "convenience-info": {
        title: "편의 정보",
        items: [
            { id: "affiliate", name: "제휴 안내", path: "/convenience-info/affiliate" },
            { id: "resident-business", name: "상주 사업 안내", path: "/convenience-info/resident-business" },
            { id: "space-adjustment", name: "공간조정회의", path: "/convenience-info/space-adjustment" }
        ]
    },
    mypage: {
        title: "내 정보",
        items: [
            { id: "edit", name: "내 정보 수정", path: "/mypage/edit" },
        ]
    },
    admin: {
        title: "관리자 페이지",
        items: [
            { id: "authorization-user", name: "회원 승인", path: "/admin/authorization-user" },
            { id: "modify-schedule", name: "일정표 변경", path: "/admin/modify-schedule" }
        ]
    }
};

const Submenus = ({ section }) => {
    const location = useLocation();
    const [expandedMenus, setExpandedMenus] = useState([]);

    // If no section is provided or the section doesn't exist in our menu data,
    // try to extract it from the current path
    let currentSection = section;
    if (!currentSection || !menuData[currentSection]) {
        const pathParts = location.pathname.split('/');
        if (pathParts.length > 1) {
            currentSection = pathParts[1];
        }
    }

    // Get the menu for the current section, or default to introduction
    const menu = menuData[currentSection] || menuData.introduction;

    // Check if the current path is in any submenu to auto-expand it
    const currentPath = location.pathname;

    // Auto expand menus containing the current path on initial render
    React.useEffect(() => {
        const autoExpandParents = [];

        menu.items.forEach(item => {
            if (item.hasSubmenu && item.subItems) {
                const hasActiveChild = item.subItems.some(subItem =>
                    currentPath === subItem.path || currentPath.startsWith(subItem.path + '/'));

                if (hasActiveChild || currentPath === item.path) {
                    autoExpandParents.push(item.id);
                }
            }
        });

        if (autoExpandParents.length > 0) {
            setExpandedMenus(autoExpandParents);
        }
    }, [currentPath, menu.items]);

    const toggleSubmenu = (itemId) => {
        setExpandedMenus(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    };

    // 현재 경로가 활성화되어 있는지 확인하는 헬퍼 함수
    const isPathActive = (path) => {
        return currentPath === path || currentPath.startsWith(path + '/');
    };

    return (
        <MenuContainer>
            <MenuTitle>{menu.title}</MenuTitle>
            <MenuList>
                {menu.items.map((item) => (
                    <MenuItem key={item.id}>
                        {item.hasSubmenu ? (
                            <>
                                {/* 서브메뉴가 있는 경우 NavLink 대신 일반 div 사용 */}
                                <ParentMenuButton
                                    isActive={isPathActive(item.path)}
                                    onClick={() => toggleSubmenu(item.id)}
                                >
                                    <span>{item.name}</span>
                                    <ExpandIcon expanded={expandedMenus.includes(item.id)}>
                                        ▼
                                    </ExpandIcon>
                                </ParentMenuButton>
                                <SubMenuList show={expandedMenus.includes(item.id)}>
                                    {item.subItems.map(subItem => (
                                        <SubMenuItem key={subItem.id}>
                                            <SubMenuLink
                                                to={subItem.path}
                                                className={({ isActive }) => isActive ? "active" : ""}
                                            >
                                                {subItem.name}
                                            </SubMenuLink>
                                        </SubMenuItem>
                                    ))}
                                </SubMenuList>
                            </>
                        ) : (
                            <MenuLink
                                to={item.path}
                                className={({ isActive }) => isActive ? "active" : ""}
                            >
                                {item.name}
                            </MenuLink>
                        )}
                    </MenuItem>
                ))}
            </MenuList>
        </MenuContainer>
    );
};

export default Submenus;